/**
 * Supabase Timeout Utility
 * Provides timeout wrapper for Supabase operations to prevent indefinite hangs
 */

/**
 * Wraps a promise with a timeout
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} operationName - Name of the operation for error messages
 * @returns {Promise} Promise that rejects with timeout error if operation exceeds timeout
 */
export function withTimeout(promise, timeoutMs, operationName) {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`${operationName} timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });
  
  return Promise.race([promise, timeoutPromise]);
}

/**
 * Determines if an error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is retryable
 */
export function isRetryableError(error) {
  if (!error) return false;
  
  const errorMessage = error.message || '';
  const errorCode = error.code || '';
  
  // Retry on timeout errors
  if (errorMessage.includes('timeout')) return true;
  
  // Retry on network errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) return true;
  
  // Retry on specific error codes
  const retryableCodes = [
    'PGRST116', // Not found (might be transient)
    '42P17',    // Infinite recursion (shouldn't happen but retry anyway)
    'PGRST301', // Connection error
  ];
  
  if (retryableCodes.includes(errorCode)) return true;
  
  return false;
}

/**
 * Delays execution by specified milliseconds
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after delay
 */
export function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
